import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Button,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  useDisclosure,
  HStack,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiCopy } from 'react-icons/fi';

const API_BASE = '/api/v1';

function APIKeys() {
  const toast = useToast();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [keyForm, setKeyForm] = useState({ name: '', description: '', is_active: true });
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [keyToDelete, setKeyToDelete] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchKeys = () => {
    fetch(`${API_BASE}/api-keys/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setKeys(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const openAddKey = () => {
    setEditingKey(null);
    setKeyForm({ name: '', description: '', is_active: true });
    onModalOpen();
  };

  const openEditKey = (key) => {
    setEditingKey(key);
    setKeyForm({
      name: key.name,
      description: key.description || '',
      is_active: key.is_active,
    });
    onModalOpen();
  };

  const handleSaveKey = async () => {
    const method = editingKey ? 'PUT' : 'POST';
    const url = editingKey ? `${API_BASE}/api-keys/${editingKey.id}/` : `${API_BASE}/api-keys/`;
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(keyForm),
      });
      const data = await res.json();
      if (res.ok) {
        fetchKeys();
        toast({ title: editingKey ? 'API key updated' : 'API key created', status: 'success', duration: 3000 });
        onModalClose();
      } else {
        toast({ title: data.error || 'Error saving API key', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving API key', status: 'error', duration: 3000 });
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast({ title: 'API key copied to clipboard', status: 'success', duration: 2000 });
  };

  const confirmDelete = (key) => {
    setKeyToDelete(key);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!keyToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/api-keys/${keyToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchKeys();
        toast({ title: 'API key deleted', status: 'success', duration: 3000 });
      } else {
        toast({ title: 'Error deleting API key', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting API key', status: 'error', duration: 3000 });
    }
    onDeleteClose();
    setKeyToDelete(null);
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="orange.500" /></Box>;
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">API Keys</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="orange" onClick={openAddKey}>
          Add API Key
        </Button>
      </HStack>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Key</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {keys.map(key => (
              <Tr key={key.id}>
                <Td fontWeight="medium">{key.name}</Td>
                <Td>
                  <HStack>
                    <Text fontSize="sm" fontFamily="mono" noOfLines={1} maxW="200px">{key.key}</Text>
                    <IconButton
                      icon={<FiCopy />}
                      size="xs"
                      variant="ghost"
                      onClick={() => copyKey(key.key)}
                      aria-label="Copy"
                    />
                  </HStack>
                </Td>
                <Td>
                  <Badge colorScheme={key.is_active ? 'green' : 'red'}>
                    {key.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </Td>
                <Td>{new Date(key.created_at).toLocaleDateString()}</Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FiEdit2 />}
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditKey(key)}
                      aria-label="Edit"
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => confirmDelete(key)}
                      aria-label="Delete"
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {keys.length === 0 && (
          <Text color="gray.500" textAlign="center" py={8}>No API keys found</Text>
        )}
      </Box>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingKey ? 'Edit API Key' : 'Add API Key'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Name</FormLabel>
              <Input
                value={keyForm.name}
                onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })}
                placeholder="API Key Name"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={keyForm.description}
                onChange={(e) => setKeyForm({ ...keyForm, description: e.target.value })}
                placeholder="Description"
                rows={3}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <FormLabel mb={0}>Active</FormLabel>
              <Switch
                isChecked={keyForm.is_active}
                onChange={(e) => setKeyForm({ ...keyForm, is_active: e.target.checked })}
                colorScheme="orange"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onModalClose}>Cancel</Button>
            <Button colorScheme="orange" onClick={handleSaveKey}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete API key "{keyToDelete?.name}"?
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default APIKeys;
